import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Headset, Truck, Handshake, ArrowRight, Check } from "lucide-react";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLES = [
  { id: "Dispatcher", icon: Headset, blurb: "Run the control tower — coordinate loads, drivers and customers in real time.",
    perks: ["Salary + performance bonus", "Hybrid schedule", "Modern TMS stack"] },
  { id: "Company Driver", icon: Truck, blurb: "Pull premium dry-van & reefer freight in late-model, well-maintained equipment.",
    perks: ["Top-of-market pay", "Consistent home time", "Full benefits + APU trucks"] },
  { id: "Owner-Operator", icon: Handshake, blurb: "Partner with Conexa and keep more of every mile you run.",
    perks: ["High revenue share", "Fuel & insurance programs", "Steady freight, no forced dispatch"] },
];

const empty = { name: "", email: "", phone: "", role: "", experience: "", location: "", message: "" };

export default function Careers() {
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const pickRole = (role) => {
    set("role", role);
    document.querySelector("#careers-form")?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.role) {
      toast.error("Please add your name, email and the role you want.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/careers`, form);
      toast.success("Application received! Our recruiting team will be in touch soon.");
      setForm(empty);
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="careers" data-testid="careers-section" className="relative py-28 md:py-40 px-6 md:px-10">
      <div className="max-w-7xl mx-auto">
        <div className="max-w-2xl">
          <span className="font-mono-x text-primary text-xs tracking-[0.35em]">/ 10 — CAREERS</span>
          <h2 className="mt-6 font-heading font-extrabold tracking-tighter text-4xl md:text-6xl leading-[0.9]">
            Drive with<br /><span className="text-primary">Conexa Relay.</span>
          </h2>
          <p className="mt-6 text-muted-foreground">
            We're hiring dispatchers, company drivers and owner-operators across our North-American network.
            Great equipment, honest miles and a team that treats the road like a craft.
          </p>
        </div>

        <div className="mt-14 grid md:grid-cols-3 gap-6">
          {ROLES.map((r, i) => (
            <motion.div key={r.id} data-testid={`role-card-${r.id}`}
              initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.6, delay: i * 0.1, ease: [0.33, 1, 0.68, 1] }}
              className="group rounded-3xl border border-border bg-card/40 p-7 flex flex-col hover:border-primary/50 transition-colors">
              <span className="w-12 h-12 rounded-2xl bg-primary/10 text-primary flex items-center justify-center mb-5">
                <r.icon className="w-5 h-5" />
              </span>
              <h3 className="font-heading font-bold text-xl">{r.id}</h3>
              <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{r.blurb}</p>
              <ul className="mt-5 space-y-2 flex-1">
                {r.perks.map((p) => (
                  <li key={p} className="flex items-center gap-2 text-sm text-foreground/80">
                    <Check className="w-4 h-4 text-primary shrink-0" /> {p}
                  </li>
                ))}
              </ul>
              <button data-testid={`role-apply-${r.id}`} onClick={() => pickRole(r.id)}
                className="mt-6 inline-flex items-center justify-center gap-2 rounded-full border border-border px-5 py-3 text-sm font-semibold group-hover:bg-primary group-hover:text-primary-foreground group-hover:border-primary transition-colors">
                Apply as {r.id} <ArrowRight className="w-4 h-4" />
              </button>
            </motion.div>
          ))}
        </div>

        <form id="careers-form" onSubmit={submit} data-testid="careers-form"
          className="mt-16 glass rounded-3xl p-8 max-w-3xl mx-auto space-y-4">
          <h3 className="font-heading font-bold text-2xl">Apply to join the fleet</h3>
          <p className="text-sm text-muted-foreground -mt-2">Tell us a little about you — it takes under a minute.</p>
          <div className="grid sm:grid-cols-2 gap-4">
            <Input data-testid="careers-name" placeholder="Full name *" value={form.name} onChange={(e) => set("name", e.target.value)} className="bg-background/50 h-12" />
            <Input data-testid="careers-email" type="email" placeholder="Email *" value={form.email} onChange={(e) => set("email", e.target.value)} className="bg-background/50 h-12" />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <Input data-testid="careers-phone" placeholder="Phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} className="bg-background/50 h-12" />
            <Select value={form.role} onValueChange={(v) => set("role", v)}>
              <SelectTrigger data-testid="careers-role" className="bg-background/50 h-12"><SelectValue placeholder="Role you want *" /></SelectTrigger>
              <SelectContent>
                {ROLES.map((r) => <SelectItem key={r.id} value={r.id} data-testid={`role-opt-${r.id}`}>{r.id}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <Input data-testid="careers-experience" placeholder="Years of experience" value={form.experience} onChange={(e) => set("experience", e.target.value)} className="bg-background/50 h-12" />
            <Input data-testid="careers-location" placeholder="Home base (city, province/state)" value={form.location} onChange={(e) => set("location", e.target.value)} className="bg-background/50 h-12" />
          </div>
          <Textarea data-testid="careers-message" placeholder="Anything else we should know? (CDL/AZ class, endorsements, equipment...)" value={form.message} onChange={(e) => set("message", e.target.value)} className="bg-background/50 min-h-[100px]" />
          <button type="submit" disabled={loading} data-testid="careers-submit"
            className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-primary text-primary-foreground font-semibold py-4 hover:shadow-[0_0_40px_hsl(var(--primary)/0.5)] transition-shadow disabled:opacity-60">
            {loading ? "Sending..." : "Submit Application"} <ArrowRight className="w-4 h-4" />
          </button>
        </form>
      </div>
    </section>
  );
}
